import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'components/ui/button'
import { MapPin, FolderOpen, ListTodo, Users, CheckCircle2 } from 'lucide-react'
import { advanceOnboardingStep, ONBOARDING_STEPS } from 'slices/onboardingSlice'

interface WelcomeStepProps {
	userName: string
	onSkip: () => void
}

const WelcomeStep: React.FC<WelcomeStepProps> = ({ userName, onSkip }) => {
	const dispatch = useDispatch()
	const user = useSelector((state: any) => state.userLogin.userInfo)
	const { saving } = useSelector((state: any) => state.onboarding)

	const firstName = userName?.split(' ')[0] || userName

	const handleNext = () => {
		dispatch(
			advanceOnboardingStep({
				nextStep: ONBOARDING_STEPS.PROFILE,
				userId: user._id,
			}),
		)
	}

	const steps = [
		{
			icon: <Users className="w-4 h-4 text-[#0080fe]" />,
			text: 'Complete your profile so your team can recognize you',
		},
		{
			icon: <MapPin className="w-4 h-4 text-[#0080fe]" />,
			text: 'Create your first location — the focal point for your deals',
		},
		{
			icon: <FolderOpen className="w-4 h-4 text-[#0080fe]" />,
			text: 'Set up your first project to organize deal activity',
		},
		{
			icon: <ListTodo className="w-4 h-4 text-[#0080fe]" />,
			text: 'Learn about tasks, partners, and collaboration tools',
		},
	]

	return (
		<div className="p-6 flex flex-col items-center text-center">
			<h2 className="text-2xl font-semibold mb-1">
				Welcome to{' '}
				<span className="text-[#052852]">MY</span>
				<span className="text-[#25a4fc]">Deal</span>
				<span className="text-[#074f9b]">Teams</span>
				{firstName ? `, ${firstName}` : ''}!
			</h2>

			<p className="text-gray-500 text-sm mb-6">
				Let's do a quick setup to get you started. This will only take a few minutes.
			</p>

			<div className="w-full space-y-3 mb-8 text-left">
				{steps.map((step, i) => (
					<div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
						<div className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-full bg-[#0080fe]/10 flex items-center justify-center">
							{step.icon}
						</div>
						<span className="text-sm text-gray-700">{step.text}</span>
					</div>
				))}
			</div>

			<div className="flex flex-col gap-2 w-full max-w-xs">
				<Button onClick={handleNext} isLarge disabled={saving} className="w-full">
					{saving ? 'Starting...' : "Let's Get Started"}
				</Button>
				<Button variant="ghost" onClick={onSkip} disabled={saving} className="w-full text-gray-500">
					Skip Setup
				</Button>
			</div>
		</div>
	)
}

export default WelcomeStep
